import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import type { ComponentType } from 'react';
import type { NavBanner, NavRole, NavSection, ResolvedNavItem } from '../config/navConfig';
import {
    IconAlertCircle,
    IconAlertTriangle,
    IconDailyBriefing,
    IconEquipment,
    IconForms,
    IconHome,
    IconProjectManager,
    IconReports,
    IconResources,
    IconSupport,
    IconTires,
    IconToggleSlider,
    IconUpload,
} from '@icons';
import '../css/LeftNavigation.css';

type NavIconComponent = ComponentType<{ iconClasses?: string }>;

const NAV_ICONS_BY_ID: Partial<Record<string, NavIconComponent>> = {
    home: IconHome,
    equipment: IconEquipment,
    forms: IconForms,
    issues: IconAlertTriangle,
    pm: IconProjectManager,
    tires: IconTires,
    'daily-briefing': IconDailyBriefing,
    reports: IconReports,
    resources: IconResources,
    settings: IconToggleSlider,
    'settings-staff': IconToggleSlider,
    'get-help': IconAlertCircle,
    inbox: IconSupport,
    logout: IconUpload,
};

const NAV_ICONS_BY_PATH: Record<string, NavIconComponent> = {
    'home-line-svgrepo-com.svg': IconHome,
    'sideNav-icons/construction-dump-dumper-svgrepo-com.svg': IconEquipment,
    'sideNav-icons/checklist-svgrepo-com.svg': IconForms,
    'sideNav-icons/warning-triangle-svgrepo-com.svg': IconAlertTriangle,
    'sideNav-icons/pm-icon.svg': IconProjectManager,
    'sideNav-icons/tire-maintenance-icon.svg': IconTires,
    'sideNav-icons/daily-briefing-icon.svg': IconDailyBriefing,
    'sideNav-icons/documents-document-svgrepo-com.svg': IconResources,
    'sideNav-icons/documents-svgrepo.svg': IconReports,
    'sideNav-icons/settings-icon.svg': IconToggleSlider,
    'sideNav-icons/logout-svgrepo-com.svg': IconUpload,
    'sideNav-icons/get-help-icon.svg': IconAlertCircle,
    'sideNav-icons/inbox-icon.svg': IconSupport,
};

const NAV_ICON_EXTRA_CLASSES: Partial<Record<string, string>> = {
    logout: 'left-nav-item-icon--rotate-right',
};

export function isLeftNavItemActive(item: ResolvedNavItem, pathname: string): boolean {
    if (item.href === '/') {
        return pathname === '/';
    }

    if (item.id === 'issues') {
        return (
            pathname === '/issues' ||
            pathname.startsWith('/issues/') ||
            pathname.startsWith('/issue/')
        );
    }

    if (item.id === 'reports') {
        return (
            pathname === '/reports' ||
            pathname.startsWith('/reports/') ||
            pathname.startsWith('/report/') ||
            pathname.startsWith('/lastreport/') ||
            pathname.startsWith('/safety-report/')
        );
    }

    if (item.id === 'users') {
        return (
            pathname === '/users' ||
            pathname.startsWith('/users/') ||
            pathname.startsWith('/user/')
        );
    }

    return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

export interface LeftNavigationViewProps {
    companyName: string;
    companyLogoUrl?: string | null;
    userName?: string;
    sections: NavSection[];
    role?: NavRole;
    roleBadge?: { label: string; description: string };
    banner?: NavBanner;
    pathname: string;
    badgeCounts?: Partial<Record<'issues' | 'inbox' | 'companies', number>>;
    onNavigate: (href: string) => void;
    onLogoutClick: () => void;
    onAccountSettingsClick: () => void;
    showLogoutModal?: boolean;
    onCancelLogout?: () => void;
    onConfirmLogout?: () => void;
}

/**
 * Presentational left nav chrome. No auth, GraphQL, or app context —
 * suitable for Storybook AppShell docs. The live app wraps this in `LeftNavigation`.
 */
export const LeftNavigationView = ({
    companyName,
    companyLogoUrl,
    userName,
    sections,
    role,
    roleBadge,
    banner,
    pathname,
    badgeCounts = {},
    onNavigate,
    onLogoutClick,
    onAccountSettingsClick,
    showLogoutModal = false,
    onCancelLogout,
    onConfirmLogout,
}: LeftNavigationViewProps) => {
    const getBadgeCount = (badgeKey?: string): number => {
        if (!badgeKey) return 0;
        if (badgeKey === 'issues' || badgeKey === 'inbox' || badgeKey === 'companies') {
            return badgeCounts[badgeKey] ?? 0;
        }
        return 0;
    };

    const renderNavIcon = (item: ResolvedNavItem) => {
        const Icon = NAV_ICONS_BY_ID[item.id] ?? NAV_ICONS_BY_PATH[item.icon];
        if (Icon) {
            const extraClasses = NAV_ICON_EXTRA_CLASSES[item.id] ?? '';
            return <Icon iconClasses={`left-nav-item-icon ${extraClasses}`.trim()} />;
        }

        return <img src={`/${item.icon}`} alt="" className="left-nav-item-icon" />;
    };

    const renderNavItem = (item: ResolvedNavItem) => {
        const badgeCount = getBadgeCount(item.badgeKey);

        if (item.isLogout) {
            return (
                <button
                    key={item.id}
                    type="button"
                    className="left-nav-item left-nav-item--button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onLogoutClick();
                    }}
                >
                    <span className="left-nav-item-left">
                        {renderNavIcon(item)}
                        <span className="left-nav-item-text">{item.text}</span>
                    </span>
                </button>
            );
        }

        if (item.state === 'locked') {
            return (
                <NavLink
                    key={item.id}
                    to={item.href}
                    className="left-nav-item locked"
                    onClick={(event) => {
                        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                            return;
                        }
                        event.preventDefault();
                        onNavigate(item.href);
                    }}
                >
                    <span className="left-nav-item-left">
                        {renderNavIcon(item)}
                        <span className="left-nav-item-text">{item.text}</span>
                    </span>
                    <span className="left-nav-item-right">
                        <span className="left-nav-lock-icon">🔒</span>
                    </span>
                </NavLink>
            );
        }

        return (
            <NavLink
                key={item.id}
                to={item.href}
                className={`left-nav-item ${isLeftNavItemActive(item, pathname) ? 'active' : ''}`}
                onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                        return;
                    }

                    event.preventDefault();
                    onNavigate(item.href);
                }}
            >
                <span className="left-nav-item-left">
                    {renderNavIcon(item)}
                    <span className="left-nav-item-text">{item.text}</span>
                </span>
                {badgeCount > 0 && (
                    <span className="left-nav-item-right">
                        <span className="left-nav-badge">{badgeCount}</span>
                    </span>
                )}
            </NavLink>
        );
    };

    return (
        <>
            <nav className="left-nav">
                <div className="left-nav-header">
                    <img
                        src={companyLogoUrl || '/logo-images/teiq-logo.png'}
                        alt={companyName ? `${companyName} logo` : 'Company logo'}
                        className="left-nav-logo"
                    />
                    <div className="left-nav-header-info">
                        <div className="left-nav-company-name">{companyName || 'TotalEquipIQ'}</div>
                        <div className="left-nav-role-subtitle">{userName}</div>
                    </div>
                    <button
                        className="left-nav-avatar-btn"
                        title="Account Settings"
                        aria-label="Account Settings"
                        type="button"
                        onClick={() => onAccountSettingsClick()}
                    >
                        {userName ? userName.charAt(0).toUpperCase() : '?'}
                    </button>
                </div>

                {banner && (
                    <div className={`left-nav-banner ${banner.type}`}>
                        {banner.type === 'paused' && (
                            <div className="left-nav-banner-title">⚠ ACCOUNT PAUSED</div>
                        )}
                        <div className="left-nav-banner-message">{banner.message}</div>
                        {banner.ctaText && (
                            <a
                                className="left-nav-banner-cta"
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onNavigate(banner.ctaHref || '/');
                                }}
                            >
                                {banner.ctaText}
                            </a>
                        )}
                    </div>
                )}

                <div className="left-nav-sections">
                    {sections.map((section) => (
                        <div key={section.id} className="left-nav-section">
                            {section.label && <div className="left-nav-section-label">{section.label}</div>}
                            {section.items.map(renderNavItem)}
                            {section.id === 'main' && <div className="left-nav-spacer" />}
                        </div>
                    ))}
                </div>

                {role === 'businessAdmin' && roleBadge && (
                    <div className="left-nav-role-badge">
                        <div className="left-nav-role-badge-label">{roleBadge.label}</div>
                        {roleBadge.description && (
                            <div className="left-nav-role-badge-desc">{roleBadge.description}</div>
                        )}
                    </div>
                )}
            </nav>

            {showLogoutModal &&
                createPortal(
                    <div
                        className="left-nav-logout-modal-overlay"
                        role="presentation"
                        onClick={() => onCancelLogout?.()}
                    >
                        <div
                            className="left-nav-logout-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="left-nav-logout-title"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 id="left-nav-logout-title">Log Out?</h3>
                            <p>Are you sure you want to log out of your account?</p>
                            <div className="left-nav-logout-modal-actions">
                                <button
                                    type="button"
                                    className="left-nav-logout-cancel"
                                    onClick={() => onCancelLogout?.()}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="left-nav-logout-confirm"
                                    onClick={() => onConfirmLogout?.()}
                                >
                                    Log Out
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
};
